import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  markReleaseAnnouncementsSeen,
  releaseVersions,
  ReleaseAnnouncementSlide,
  ReleaseModule,
  selectUnseenReleaseAnnouncements,
} from '@/config/releases';
import queryKeys from '@/constants/queryKeys';
import {
  useGetUserOnboardingQuery,
  useSaveUserOnboardingMutation,
  UserOnboardingData,
} from '@/hooks/api/useUserOnboarding';

interface UseReleaseAnnouncementsOptions {
  enabled?: boolean;
}

const EMPTY_LAST_SEEN_RELEASE_VERSION_BY_MODULE: Record<string, number> = {};

export const useReleaseAnnouncements = (
  releaseModule: ReleaseModule,
  { enabled = true }: UseReleaseAnnouncementsOptions = {},
) => {
  const queryClient = useQueryClient();
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissedBatchKey, setDismissedBatchKey] = useState<string | null>(
    null,
  );

  const { data: onboardingData, isFetched: isOnboardingFetched } =
    useGetUserOnboardingQuery(enabled);

  const lastSeenReleaseVersionByModule =
    onboardingData?.lastSeenReleaseVersionByModule ??
    EMPTY_LAST_SEEN_RELEASE_VERSION_BY_MODULE;
  const lastSeenReleaseVersion =
    lastSeenReleaseVersionByModule[releaseModule] ?? 0;

  const unseenAnnouncements = useMemo(
    () =>
      selectUnseenReleaseAnnouncements(
        releaseVersions,
        lastSeenReleaseVersion,
        releaseModule,
      ),
    [lastSeenReleaseVersion, releaseModule],
  );

  const batchKey = useMemo(
    () =>
      unseenAnnouncements
        .map((release) => `${release.id}:${release.releaseVersion}`)
        .join('|'),
    [unseenAnnouncements],
  );

  useEffect(() => {
    setActiveIndex(0);
    setDismissedBatchKey(null);
  }, [batchKey]);

  const saveSeenMutation = useSaveUserOnboardingMutation();
  const isOpen =
    enabled &&
    isOnboardingFetched &&
    unseenAnnouncements.length > 0 &&
    dismissedBatchKey !== batchKey;

  const activeAnnouncement = unseenAnnouncements[activeIndex] ?? null;
  const isLastAnnouncement =
    activeIndex >= Math.max(unseenAnnouncements.length - 1, 0);

  const markBatchSeen = useCallback(
    async (announcements?: ReleaseAnnouncementSlide[]) => {
      const announcementsToMark = Array.isArray(announcements)
        ? announcements
        : unseenAnnouncements;

      if (!announcementsToMark.length) return;

      const nextLastSeenReleaseVersion = markReleaseAnnouncementsSeen(
        lastSeenReleaseVersion,
        announcementsToMark,
      );
      const nextLastSeenReleaseVersionByModule = {
        ...lastSeenReleaseVersionByModule,
        [releaseModule]: nextLastSeenReleaseVersion,
      };

      try {
        await saveSeenMutation.mutateAsync({
          onboardingData: {
            lastSeenReleaseVersionByModule: nextLastSeenReleaseVersionByModule,
          },
        });

        setDismissedBatchKey(batchKey);
        setActiveIndex(0);

        queryClient.setQueryData<UserOnboardingData>(
          queryKeys.GET_USER_ONBOARDING,
          (currentData) => ({
            ...(currentData ?? {}),
            lastSeenReleaseVersionByModule: nextLastSeenReleaseVersionByModule,
          }),
        );
      } catch (error) {
        console.error('Failed to sync release announcement dismissal:', error);
      }
    },
    [
      batchKey,
      queryClient,
      saveSeenMutation,
      lastSeenReleaseVersion,
      lastSeenReleaseVersionByModule,
      releaseModule,
      unseenAnnouncements,
    ],
  );

  const nextAnnouncement = useCallback(() => {
    if (isLastAnnouncement) {
      markBatchSeen();
      return;
    }

    setActiveIndex((currentIndex) =>
      Math.min(currentIndex + 1, unseenAnnouncements.length - 1),
    );
  }, [isLastAnnouncement, markBatchSeen, unseenAnnouncements.length]);

  const previousAnnouncement = useCallback(() => {
    setActiveIndex((currentIndex) => Math.max(currentIndex - 1, 0));
  }, []);

  return {
    activeAnnouncement,
    activeIndex,
    announcements: unseenAnnouncements,
    isFirstAnnouncement: activeIndex === 0,
    isLastAnnouncement,
    isOpen,
    isSaving: saveSeenMutation.isLoading,
    markBatchSeen,
    nextAnnouncement,
    previousAnnouncement,
  };
};
